
import img0 from './Images/OnData_Role_Configuration_Okta.png';
import img1 from './Images/OnData_Data_Access_Role_Configuration_Okta.png';
import img2 from './Images/Assign_Roles_to_Groups_Okta.png';
  
const content = `# Assigning Roles in Okta

OnData supports the integration with Identity and Access Management (IAM) systems for user authentication and authorization. Both the OnData application roles and the 
data access roles can be configured in your IAM system, so that you can use your IAM system to control both user's access to the OnData application and user's access to 
your sensitive data. For details regarding the OnData application roles, please refer to [**OnData Application Roles**](reference_doc#ondata_portal/ondata_application_roles). For details on how 
to configure data access roles, please refer to [**Data Access Roles**](reference_doc#ondata_portal/data_access_roles). The purpose of this document is to detail the process of assigning 
application roles and data access roles to users and user groups in Okta. The process to assign such roles within other types of IAM systems can be similar.  

## Prerequisite
a. An Okta user account with at least one of the following roles: Super Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  
c. The OnData Portal has been integrated with your Okta tenant for Single Sign-On (SSO) purpose. For details regarding Okta integration, please refer to 
[**Okta Integration Using OIDC**](reference_doc#ondata_portal/sso_integration/okta_integration_using_oidc) or [**Okta Integration Using SAML**](reference_doc#ondata_portal/sso_integration/okta_integration_using_saml) .  

## Configure App Roles for OnData Application
a. Login to the Okta Admin Console, go to “Directory” -> “Profile Editor”  
b. Select "Apps" under the "Users" list, and from the list of applications, select the "OnData Portal User" application  
c. Find the custom attribute named "OnData App Roles", and click on the edit icon  
d. For each application role configured at the OnData Portal, including the built-in roles and custom roles, if such role is needed for your organization, follow the 
steps below to add that role to the list:
   * Click on "Add Another" under "Attribute members"  
   * Enter a "Display name" and the corresponding role value for the "Value" field that is the same as the role value configured at the OnData Portal. Below is the list of default roles configured at the OnData Portal:  
  
| Display Name | Role Value | Description |
| ------------ | ---------- | ----------- |
| System Administrator | SystemAdministrator | A system administrator has full access to all features. |
| Data Analyst | DataAnalyst | A data analyst manages data catalog, data ownership and data encryption/obfuscation rules. |
| Data Management Officer | DataManagementOfficer | A data management officer manages data classifications, data access roles, data ownership, data catalog and data encryption/obfuscation rules. |
| Database Administrator | DatabaseAdministrator | A database administrator manages database related operations. |
| SecureFile Only | SecureFileOnly | A limited user role which restricts it's users to only have access to the SecureFile desktop app and user profile page on Portal. **Note:** Please do not combine the SecureFile Only role with any other roles when assigning it to users or user groups. |
| Security Administrator | SecurityAdministrator | A security administrator manages the network and security related features. |
| User | User | A generic user role with no special permission to the system. |

   * Click "Save Attribute" to save all desired application roles  
  
![OnData Role Configuration in Okta](${ img0 })  
    
## Configure Data Access Roles
a. Login to the Okta Admin Console, go to “Directory” -> “Profile Editor”  
b. Select "Apps" under the "Users" list, and from the list of applications, select the "OnData Portal User" application  
c. Click “Add Attribute” to add the data access role attribute with the following options  
- Data type: string array
- Display name: OnData Data Access Roles
- Variable name: DataAccessRoles
- Enum: check the “Define enumerated list of values” option. For each data access role configured at the OnData Portal, click on "Add Another" under 
"Attribute members", and enter a "Display name" and the corresponding data access role value for the "Value" field that is the same as the data access role 
value configured at the OnData Portal
- Attribute required: check the “Yes” option
- Attribute type: select the “Group” option
- Group Priority: select the "Combine values across groups" option
- Leave all other fields as default and click “Save”
  
![OnData Data Access Role Configuration in Okta](${ img1 })  

d. If the OIDC protocol is used for the SSO configuration between OnData and Okta, do the following
- Login to the Okta Admin Console, go to “Security” -> “API”
- Click “default” (or whatever the authorization server is used) under the “Authorization Servers” tab  
- Under the “Claims” tab, click “Add Claim” to add a new claim with the following options  
  - Name: DataAccessRoles
  - Include in token type: ID Token
  - Value type: Expression
  - Value: appuser.DataAccessRoles
- Leave all other fields as default and click “Create”

e. If the SAML protocol is used for the SSO configuration between OnData and Okta, do the following
- Login to the Okta Admin Console, go to “Applications” -> “Applications”
- From the list of applications, select the "OnData Portal" application
- Find the “SAML Settings” section under the “General” tab, and click “Edit” and then click “Next”
- Under “Attribute Statements”, click "Add Another"
- Enter “DataAccessRoles” as the “Name”, and “appuser.DataAccessRoles” as the “Value”  
- Click “Next” and “Finish” to complete 
  
## Assign OnData App Roles and Data Access Roles to User Groups
Once the OnData application roles and the data access roles are configured in Okta, those roles can be assigned to your users 
and user groups. Below are the steps:  
a. Login to the Okta Admin Console, go to “Applications” -> “Applications”  
b. From the list of applications, select the "OnData Portal" application  
c. If the SAML protocol is used, skip this step. If the OpenID Connect protocol is used, select the "Assignments" tab  
d. Select the "Groups" filter on the left, if the group to be managed is in the list already  
- Click on the edit icon on that group
- Select the appropriate "OnData App Roles" and "OnData Data Access Roles" for this group (**Note:** Please do not combine the "SecureFile Only" App Role with any other 
OnData App Roles when assigning it to users or user groups)
- Click "Save" to complete the assignment
  
![Assign Roles to Groups in Okta](${ img2 })  
  
e. If the group to be managed is not in the list yet  
- Under the “Assignments” tab, click "Assign" -> "Assign to Groups"  
- Find the group to be managed, click "Assign"
- Select the appropriate "OnData App Roles" and "OnData Data Access Roles" for this group (**Note:** Please do not combine the "SecureFile Only" App Role with any other 
OnData App Roles when assigning it to users or user groups)
- Click "Save and Go Back" to complete the assignment  
  
A user can be assigned one or more OnData App Roles and Data Access Roles. When multiple roles are assigned, the permissions for that user will be the combined list of 
permissions from all assigned roles.  
  
  `
  
export default content;
